// The Impact landing strip's "Recommended For You" slice.
//
// Separate from `impactStore` (schedules, runs, quota, profiles) because the two have unrelated
// lifecycles: the run list is refreshed by SSE terminal frames and by every schedule mutation, the
// recommendation scan by neither. Generation counter + scope guard mirror `impactStore`'s own.
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { CausalScope } from '../api/CausalApi'
import { impactRecommendationApi } from '../api/RecommendationApi'
import type { ImpactRecommendationSection } from '../models/recommendation.types'
import { mapExecutionToSection } from '../service/impactRecommendations'
import { recommendationDismissal } from '../storage/recommendationDismissal'

import { ApiError } from '@/shared/http'
import type { UiAsyncState } from '@/shared/ui'

function isCanceled(cause: unknown): boolean {
  return ApiError.isApiError(cause) && cause.kind === 'canceled'
}

export const useImpactRecommendationStore = defineStore('impactRecommendations', () => {
  const section = ref<ImpactRecommendationSection | null>(null)
  const state = ref<UiAsyncState>('empty')
  const dismissed = ref(false)

  let generation = 0
  let scope: CausalScope | null = null

  /** v3 rendered two pulsing placeholder cards while the scan was being read. */
  const isLoading = computed(() => state.value === 'loading')
  const hasRecommendations = computed(() => (section.value?.recommendations.length ?? 0) > 0)
  /** What the page actually renders: cards exist, and this run has not been dismissed. */
  const isVisible = computed(() => hasRecommendations.value && !dismissed.value)

  function syncDismissed(): void {
    const identity = section.value?.identity
    dismissed.value =
      identity && scope ? recommendationDismissal.isDismissed(scope, identity) : false
  }

  async function loadLatest(
    nextScope: CausalScope,
    options: { signal?: AbortSignal } = {},
  ): Promise<void> {
    const requestGeneration = ++generation
    scope = nextScope
    state.value = 'loading'
    try {
      const execution = await impactRecommendationApi.getLatest(nextScope, options.signal)
      if (requestGeneration !== generation || options.signal?.aborted) return
      section.value = mapExecutionToSection(execution)
      state.value = section.value?.recommendations.length ? 'ready' : 'empty'
      syncDismissed()
    } catch (cause) {
      if (requestGeneration !== generation || options.signal?.aborted || isCanceled(cause)) return
      // Legacy parity: the strip renders nothing on failure. v3 set an `errorMessage` here that no
      // template ever read, so there is no error surface to port — just an absent section.
      section.value = null
      state.value = 'error'
      dismissed.value = false
    }
  }

  /** Hide this run, and remember it (per account + workspace + run identity). */
  function dismiss(): void {
    const identity = section.value?.identity
    if (!identity) return
    dismissed.value = true
    if (scope) recommendationDismissal.dismiss(scope, identity)
  }

  function resetState(): void {
    generation += 1
    scope = null
    section.value = null
    state.value = 'empty'
    dismissed.value = false
  }

  return {
    section,
    state,
    dismissed,
    isLoading,
    hasRecommendations,
    isVisible,
    loadLatest,
    dismiss,
    resetState,
  }
})

export type ImpactRecommendationStore = ReturnType<typeof useImpactRecommendationStore>
